import React from "react";
import { useTranslation } from "react-i18next";

import { ACTION_ICONS } from "../View";
import { CUSTOM_ICON_NEW_KEY } from "../shared/constants";
import { QuickOption } from "./types";
import {
    Card,
    SectionHeader,
    SectionTitle,
    ToggleContainer,
    ToggleLabel,
    Toggle,
    SmallToggle,
    LanguageTabs,
    LanguageTab,
    InputRow,
    InputLabel,
    Input,
    InputWrapper,
    ResetButton,
    ResetAllButton,
    OptionRow,
    OptionIconCircle,
    OptionTitle,
    DeleteButton,
    AddOptionSection,
    AddOptionButton,
    IconPickerGrid,
    IconPickerItem,
    AddFormRow,
    ValidateButton,
    CancelButton,
    DisabledOverlay,
    UploadIconButton,
    CustomIconPreview,
} from "./styled";

// ─── SVG Icons ──────────────────────────────────────────

const ResetIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
    </svg>
);

const DeleteIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
    </svg>
);

type QuickOptionsCardProps = {
    mainColor: string;
    languages: Array<{ code: string; label: string }>;
    selectedLang: string;
    onSelectLang: (lang: string) => void;
    quickOptions: QuickOption[];
    defaultQuickOptions: QuickOption[];
    serverCallEnabled: boolean;
    effectiveServerCallEnabled: boolean;
    onToggleServerCall: () => void;
    onToggleOption: (id: string) => void;
    onDeleteOption: (id: string) => void;
    onOptionTitleChange: (id: string, value: string) => void;
    onResetOptionTitle: (id: string) => void;
    onResetAllOptions: () => void;
    // Add option form
    showAddOption: boolean;
    setShowAddOption: (value: boolean) => void;
    newOptionIcon: string;
    setNewOptionIcon: (value: string) => void;
    newOptionTitle: string;
    setNewOptionTitle: (value: string) => void;
    newCustomIconUrls: string[];
    newCustomIconSelectedIdx: number;
    setNewCustomIconSelectedIdx: (value: number) => void;
    fileInputRef: React.RefObject<HTMLInputElement>;
    onIconUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onAddOption: () => void;
};

export function QuickOptionsCard({
    mainColor,
    languages,
    selectedLang,
    onSelectLang,
    quickOptions,
    defaultQuickOptions,
    serverCallEnabled,
    effectiveServerCallEnabled,
    onToggleServerCall,
    onToggleOption,
    onDeleteOption,
    onOptionTitleChange,
    onResetOptionTitle,
    onResetAllOptions,
    showAddOption,
    setShowAddOption,
    newOptionIcon,
    setNewOptionIcon,
    newOptionTitle,
    setNewOptionTitle,
    newCustomIconUrls,
    newCustomIconSelectedIdx,
    setNewCustomIconSelectedIdx,
    fileInputRef,
    onIconUpload,
    onAddOption,
}: QuickOptionsCardProps): JSX.Element {
    const { t } = useTranslation();

    return (
        <Card data-testid="scan-to-server-call-quick-options-card">
            <SectionHeader>
                <SectionTitle>{t("Quick options")}</SectionTitle>
                <ToggleContainer>
                    <ToggleLabel>
                        {effectiveServerCallEnabled
                            ? t("Active")
                            : t("Disabled")}
                    </ToggleLabel>
                    <Toggle
                        data-testid="scan-to-server-call-enabled-toggle"
                        isActive={effectiveServerCallEnabled}
                        mainColor={mainColor}
                        onClick={onToggleServerCall}
                        aria-label={t("Enable server call")}
                    />
                </ToggleContainer>
            </SectionHeader>

            {serverCallEnabled ? (
                <>
                    <SectionTitle style={{ marginBottom: "15px" }}>
                        {t("Title and Description")}
                    </SectionTitle>

                    <LanguageTabs data-testid="scan-to-server-call-language-tabs">
                        {languages.map((lang) => (
                            <LanguageTab
                                key={lang.code}
                                data-testid={`scan-to-server-call-lang-tab-${lang.code}`}
                                isActive={selectedLang === lang.code}
                                mainColor={mainColor}
                                onClick={() => onSelectLang(lang.code)}
                            >
                                {lang.label}
                            </LanguageTab>
                        ))}
                    </LanguageTabs>

                    {/* Column headers */}
                    <InputRow
                        style={{
                            borderBottom: "1px solid #e0e0e0",
                            paddingBottom: "10px",
                            marginBottom: "5px",
                        }}
                    >
                        <InputLabel></InputLabel>
                        <InputLabel style={{ fontWeight: 500 }}>
                            {t("Default text")}
                        </InputLabel>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                width: "100%",
                            }}
                        >
                            <InputLabel style={{ fontWeight: 500 }}>
                                {t("Customized")}
                            </InputLabel>
                            <ResetAllButton
                                data-testid="scan-to-server-call-reset-all-btn"
                                mainColor={mainColor}
                                onClick={onResetAllOptions}
                            >
                                <ResetIcon />
                                {t("Reset")}
                            </ResetAllButton>
                        </div>
                    </InputRow>

                    {/* Option rows */}
                    {quickOptions.map((option) => {
                        const def = defaultQuickOptions.find(
                            (d) => d.id === option.id
                        );
                        // Built-in options: read the ship-default from
                        // defaultQuickOptions. Custom options: use the
                        // `defaultTitle` frozen at creation (see
                        // handleAddOption). Older persisted custom options
                        // may lack it — fall back to their current title
                        // so the read-only column isn't empty.
                        const defaultTitle = def
                            ? def.title[selectedLang] || def.title.fr || ""
                            : (option.defaultTitle &&
                                  (option.defaultTitle[selectedLang] ||
                                      option.defaultTitle.fr)) ||
                              option.title[selectedLang] ||
                              option.title.fr ||
                              "";
                        return (
                            <div
                                key={option.id}
                                data-testid={`scan-to-server-call-option-${option.id}`}
                            >
                                <OptionRow
                                    data-testid={`scan-to-server-call-option-row-${option.id}`}
                                >
                                    <OptionIconCircle mainColor={mainColor}>
                                        {option.customIconUrl ? (
                                            <img
                                                src={option.customIconUrl}
                                                alt=""
                                                style={{
                                                    width: 18,
                                                    height: 18,
                                                    objectFit: "contain",
                                                }}
                                            />
                                        ) : (
                                            <svg viewBox="0 0 24 24">
                                                <path
                                                    d={
                                                        ACTION_ICONS[
                                                            option.icon
                                                        ] || ACTION_ICONS.help
                                                    }
                                                />
                                            </svg>
                                        )}
                                    </OptionIconCircle>
                                    <OptionTitle>
                                        {option.title[selectedLang] ||
                                            option.title.fr ||
                                            ""}
                                    </OptionTitle>
                                    <SmallToggle
                                        data-testid={`scan-to-server-call-option-toggle-${option.id}`}
                                        isActive={option.enabled}
                                        mainColor={mainColor}
                                        onClick={() =>
                                            onToggleOption(option.id)
                                        }
                                        aria-label={t(
                                            "Toggle option visibility"
                                        )}
                                    />
                                    <DeleteButton
                                        data-testid={`scan-to-server-call-option-delete-btn-${option.id}`}
                                        onClick={() =>
                                            onDeleteOption(option.id)
                                        }
                                    >
                                        <DeleteIcon />
                                    </DeleteButton>
                                </OptionRow>
                                {/* Title edit row */}
                                <InputRow
                                    data-testid={`scan-to-server-call-option-title-row-${option.id}`}
                                >
                                    <InputLabel>{t("Title")}</InputLabel>
                                    <Input
                                        data-testid={`scan-to-server-call-option-default-title-${option.id}`}
                                        value={defaultTitle}
                                        readOnly
                                        style={{
                                            backgroundColor: "#f8f9fa",
                                            cursor: "not-allowed",
                                        }}
                                    />
                                    <InputWrapper>
                                        <Input
                                            data-testid={`scan-to-server-call-option-custom-title-${option.id}`}
                                            value={
                                                option.title[selectedLang] || ""
                                            }
                                            onChange={(e) =>
                                                onOptionTitleChange(
                                                    option.id,
                                                    e.target.value
                                                )
                                            }
                                            placeholder={defaultTitle}
                                        />
                                        {option.isDefault && (
                                            <ResetButton
                                                data-testid={`scan-to-server-call-option-reset-btn-${option.id}`}
                                                mainColor={mainColor}
                                                onClick={() =>
                                                    onResetOptionTitle(
                                                        option.id
                                                    )
                                                }
                                                title={t("Reset")}
                                            >
                                                <ResetIcon />
                                            </ResetButton>
                                        )}
                                    </InputWrapper>
                                </InputRow>
                            </div>
                        );
                    })}

                    {/* Add option section */}
                    {!showAddOption ? (
                        <div style={{ marginTop: "15px" }}>
                            <AddOptionButton
                                data-testid="scan-to-server-call-add-option-btn"
                                mainColor={mainColor}
                                onClick={() => setShowAddOption(true)}
                            >
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                                </svg>
                                {t("Add an option")}
                            </AddOptionButton>
                        </div>
                    ) : (
                        <AddOptionSection data-testid="scan-to-server-call-add-option-section">
                            <SectionTitle
                                style={{
                                    fontSize: "14px",
                                    marginBottom: "10px",
                                }}
                            >
                                {t("Add an option")}
                            </SectionTitle>
                            <InputLabel
                                style={{
                                    display: "block",
                                    marginBottom: "8px",
                                }}
                            >
                                {t("Choose an icon")}
                            </InputLabel>
                            <IconPickerGrid data-testid="scan-to-server-call-icon-picker-grid">
                                {Object.entries(ACTION_ICONS).map(
                                    ([key, path]) => (
                                        <IconPickerItem
                                            key={key}
                                            data-testid={`scan-to-server-call-icon-picker-${key}`}
                                            isActive={newOptionIcon === key}
                                            mainColor={mainColor}
                                            onClick={() => {
                                                setNewOptionIcon(key);
                                                setNewCustomIconUrl(null);
                                            }}
                                        >
                                            <svg viewBox="0 0 24 24">
                                                <path d={path} />
                                            </svg>
                                        </IconPickerItem>
                                    )
                                )}
                                {newCustomIconUrls.map((url, idx) => (
                                    <CustomIconPreview
                                        key={idx}
                                        data-testid={`scan-to-server-call-custom-icon-preview-${idx}`}
                                        isActive={
                                            newOptionIcon ===
                                                CUSTOM_ICON_NEW_KEY &&
                                            newCustomIconSelectedIdx === idx
                                        }
                                        mainColor={mainColor}
                                        onClick={() => {
                                            setNewCustomIconSelectedIdx(idx);
                                            setNewOptionIcon(
                                                CUSTOM_ICON_NEW_KEY
                                            );
                                        }}
                                    >
                                        <img src={url} alt="" />
                                    </CustomIconPreview>
                                ))}
                                {/* Upload button stays visible even after
                                    one or more icons have been uploaded —
                                    same pattern as Offres' multi-icon
                                    picker. */}
                                <UploadIconButton
                                    data-testid="scan-to-server-call-upload-icon-btn"
                                    isActive={false}
                                    mainColor={mainColor}
                                    htmlFor="icon-upload-input"
                                >
                                    <svg viewBox="0 0 24 24">
                                        <path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z" />
                                    </svg>
                                </UploadIconButton>
                                <input
                                    id="icon-upload-input"
                                    data-testid="scan-to-server-call-icon-upload-input"
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    style={{ display: "none" }}
                                    onChange={onIconUpload}
                                />
                            </IconPickerGrid>
                            <InputLabel
                                style={{
                                    display: "block",
                                    marginBottom: "8px",
                                }}
                            >
                                {t("Title")} (
                                {
                                    languages.find(
                                        (l) => l.code === selectedLang
                                    )?.label
                                }
                                )
                            </InputLabel>
                            <Input
                                data-testid="scan-to-server-call-new-option-title-input"
                                value={newOptionTitle}
                                onChange={(e) =>
                                    setNewOptionTitle(e.target.value)
                                }
                                placeholder={t("Enter option title")}
                                style={{ marginBottom: "10px" }}
                            />
                            <AddFormRow>
                                <ValidateButton
                                    data-testid="scan-to-server-call-validate-add-btn"
                                    mainColor={mainColor}
                                    onClick={onAddOption}
                                    disabled={!newOptionTitle.trim()}
                                >
                                    {t("Validate")}
                                </ValidateButton>
                                <CancelButton
                                    data-testid="scan-to-server-call-cancel-add-btn"
                                    onClick={() => {
                                        setShowAddOption(false);
                                        setNewOptionTitle("");
                                        setNewOptionIcon("restaurant");
                                        setNewCustomIconUrl(null);
                                    }}
                                >
                                    {t("Cancel")}
                                </CancelButton>
                            </AddFormRow>
                        </AddOptionSection>
                    )}
                </>
            ) : (
                <DisabledOverlay>
                    {t("Server call options are disabled")}
                </DisabledOverlay>
            )}
        </Card>
    );
}
